import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "gh-container" }
const _hoisted_2 = { class: "gh-page-margin my-3" }
const _hoisted_3 = {
  key: 1,
  class: "alert alert-danger",
  role: "alert"
}
const _hoisted_4 = /*#__PURE__*/_createElementVNode("div", { id: "checkout-iframe" }, null, -1)

import { ref, computed, onMounted, onUnmounted, onUpdated, onBeforeUnmount, type App, toRaw } from 'vue'
import { useRouter, onBeforeRouteLeave } from 'vue-router'
import { useStore } from 'vuex'
import { GSG_ENVIRONMENT } from '@grantstreet/psc-environment/environment.js'
import { useI18n } from 'vue-i18n'
import { useGsgUser } from '@grantstreet/user'
import { useLoginHelpers } from '@grantstreet/govhub-vue/src/login-helpers.js'
import { debounce } from 'debounce'
import { configState } from '@grantstreet/psc-config'
import EventBus from '@grantstreet/psc-vue/utils/event-bus.ts'
import LoadingBars from '@grantstreet/loaders-vue/LoadingBars.vue'
import { sentryException } from '@grantstreet/govhub-vue'

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions, no-unused-vars
  interface Window {
      CheckoutWidget: {
        attach: (
          el: string,
          args: {
            [key: string]: string | number | boolean | ((...args: never)=> unknown)
          }
        )=> void
      }
  }
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CheckoutPage',
  props: {
  hideTitle: {
    type: Boolean,
    default: false,
  },
},
  setup(__props) {



const { handleLogin } = useLoginHelpers()
const store = useStore()
const cartLoadPromise = computed(() => store.state.Cart.cartLoadPromise)
const cart = computed(() => store.state.Cart.cart)
const router = useRouter()
const i18n = useI18n()
const { user } = useGsgUser()
const loading = ref(true)
const hasLoadError = ref(false)

const fetchCart = debounce((cartId) => store.dispatch('Cart/loadCart', { cartId }), 500)
const fetchSchedules = debounce(() => store.dispatch('SchedPay/retrieveUserSchedules'), 500)

function syncCartStore () {
  // Push any state needed from the parent GH cart vuex store to the checkout
  // widget vuex store here
  const cartSyncPayload = {
    payableAutopayState: {},
    prepopulateCartWarnings: toRaw(store.state.Cart.prepopulateCartWarnings),
  }
  store.state.Cart.cart?.items?.forEach(item => {
    cartSyncPayload.payableAutopayState[item.payable.savePath] = item.enrollInAutopay
  })
  window.postMessage({
    action: 'cartStoreSync',
    payload: cartSyncPayload,
  }, window.location.origin)
}

const handleAutopayEnrollmentChange = (payload) => store.commit('Cart/enrollPayableInAutoPay', payload)

const handleMessage = async (event) => {
  switch (event.data?.action) {
  case 'cartUpdate':
    // The user will opt in or out of REx autopay enrollment in the checkout
    // widget, which will update the 'inner' vuex store. This state will be
    // included in 'cartUpdate' messages. We will update the 'outer' vuex store
    // here. This ensures that when payables are added to the outer vuex cart,
    // they are added with the correct autopay enrollment state.
    if (store.state.Cart.autoenrollRenewals === null && [true, false].includes(event.data.payload.autoenrollRenewals)) {
      store.dispatch('Cart/autoenrollRenewalsInAutopay', event.data.payload.autoenrollRenewals)
    }
    fetchCart(event.data.payload.cartId)
    break
  case 'schedulesUpdate':
    fetchSchedules()
    break
  case 'route':
    router.push(event.data.payload.to)
    break
  case 'eventBus':
    EventBus.$emit(event.data.payload.type, event.data.payload.data)
    break
  default:
    break
  }
}

const unmountCheckoutWidget = () => {
  type AppRoot = HTMLDivElement & {
    __vue_app__?: App
  }

  document.querySelector<AppRoot>('#checkout-iframe')?.__vue_app__?.unmount()
}

const handleError = (error: Error) => {
  hasLoadError.value = true
  loading.value = false
  console.error(error)
  sentryException(error)
}

const initCheckoutWidget = async () => {
  try {
    unmountCheckoutWidget()
    await cartLoadPromise.value

    await window.CheckoutWidget.attach('#checkout-iframe', {
      internal: true,
      site: `${configState.config.client}/${configState.config.site}`,
      environment: GSG_ENVIRONMENT,
      cartId: cart.value.id,
      language: i18n.locale.value,
      getAccessToken: user.getAccessToken,
      getUserData: () => JSON.stringify(user),
      handleLogin,
    })

    loading.value = false
  }
  catch (error) {
    let exception: Error
    if (!(error instanceof Error)) {
      exception = new Error(String(error))
    }
    else {
      exception = error
    }
    handleError(exception)
  }
}

function RegisterEventBusHandlers (destroyHandlers = false) {
  const EventBusHandlers = {
    'checkoutWidget.requestCartStoreSync': syncCartStore,
    'checkoutWidget.autopayEnrollmentStateChange': handleAutopayEnrollmentChange,
  }
  for (const [eventType, handler] of Object.entries(EventBusHandlers)) {
    EventBus.$off(eventType, handler)
    if (destroyHandlers) continue
    EventBus.$on(eventType, handler)
  }
}

onMounted(() => {
  RegisterEventBusHandlers()
  window.addEventListener('message', handleMessage)
})
onUpdated(() => {
  RegisterEventBusHandlers()
  window.removeEventListener('message', handleMessage)
  window.addEventListener('message', handleMessage)
})
onUnmounted(() => {
  RegisterEventBusHandlers(true)
  window.removeEventListener('message', handleMessage)
})
onMounted(initCheckoutWidget)
onUpdated(initCheckoutWidget)
onBeforeUnmount(unmountCheckoutWidget)
onBeforeRouteLeave(unmountCheckoutWidget)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        (loading.value)
          ? (_openBlock(), _createBlock(LoadingBars, { key: 0 }))
          : _createCommentVNode("", true),
        (hasLoadError.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_ctx.$t('error.loadFailure.checkoutWidget')), 1))
          : _createCommentVNode("", true)
      ])
    ]),
    _hoisted_4
  ], 64))
}
}

})